import { cn } from "@/lib/utils";

interface SteeringLoaderProps {
  className?: string;
  label?: string;
}

/**
 * Brand loader: steering wheel that makes small, realistic left/right
 * steering corrections (never a full rotation).
 * Optimized single-path SVG, inlined to avoid an extra network request.
 */
export function SteeringLoader({ className, label }: SteeringLoaderProps) {
  return (
    <span
      className="inline-flex items-center gap-2"
      role="status"
      aria-label={label || "Loading"}
    >
      <svg
        viewBox="0 0 446 424"
        aria-hidden="true"
        className={cn("h-6 w-6 animate-steer text-primary", className)}
        fill="currentColor"
      >
        <path d="M223 0C100 0 0 100 0 223c0 83 45.6 158.6 119.1 197.3l5.8 3.1h196.1l5.9-3.1C400.4 381.6 446 306 446 223 446 100 346 0 223 0Zm84.6 370h-41.3l-9.3-66.2c6.2-1.1 12.4-2.5 18.7-4.1 3.2-.9 5.9-3.2 7.3-6.3 20.7-45.8 28.9-88.5 25.9-134.5-.3-4.7-3.6-8.8-8.2-10-26.6-7.5-52-11.1-77.7-11.1s-51.1 3.6-77.7 11.1c-4.6 1.3-7.9 5.3-8.2 10-3 46 5.2 88.7 25.9 134.5 1.4 3.1 4.1 5.4 7.3 6.3 6.3 1.7 12.5 3 18.7 4.1l-9.3 66.2h-41.3c-36.6-21.1-63.5-54.6-76.4-93.5 22.4-3.2 44.8-5.7 67.2-7.6-8.5-25.6-13.4-50.7-14.8-76.4-22.2 5.1-43.3 13-60.9 25C56.4 126.5 131.3 53.4 223 53.4S389.6 126.6 392.5 217.5c-1.8-1.2-3.6-2.4-5.6-3.5-16.9-9.7-35.7-16.4-55.4-21-1.4 25.5-6.2 50.4-14.7 75.8 22.4 1.9 44.8 4.4 67.2 7.6-12.9 39-39.8 72.5-76.4 93.6Z" />
      </svg>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}

/**
 * Full-screen transparent overlay loader. Shows the brand steering wheel
 * centered over the current screen with a subtle dimming backdrop.
 */
export function FullScreenOverlayLoader({ className, label }: SteeringLoaderProps) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#FDF9F7]/25">
      <SteeringLoader className={cn("h-12 w-12", className)} label={label} />
    </div>
  );
}

export default SteeringLoader;
